/**
 * useTrackEvent Hook
 *
 * A React hook that provides a simple way to track analytics events from components.
 */

'use client'

import { useCallback, useEffect, useState } from 'react'
import { getConsentData } from '@/components/cookie-modal'

// Standard events recognized by Meta Pixel
const standardEvents = [
  'PageView',
  'ViewContent',
  'AddToCart',
  'InitiateCheckout',
  'AddPaymentInfo',
  'Purchase',
  'Lead',
]

// Custom events that have been previously verified by Meta
const verifiedCustomEvents = [
  'BookLastMinute',
  'BookConsultation',
  'Book',
  'InitiateCheckoutTreatment',
  'PurchaseTreatment',
]

// Track recent events to prevent duplicates within a short timeframe
const recentEvents = new Map<string, number>()
// Deduplication window in milliseconds (2 seconds)
const DEDUPLICATION_WINDOW = 2000

/**
 * Safe wrapper for fbq calls to handle potential promise rejections
 */
function safeFbq(command: string, ...args: any[]) {
  if (typeof window === 'undefined' || !window.fbq) {
    return Promise.resolve() // Return resolved promise if fbq isn't available
  }

  try {
    // The fbq function might return a promise or undefined
    const result = window.fbq(command, ...args)

    // If it's a promise, catch any rejections
    if (result && typeof result.catch === 'function') {
      return result.catch((error: any) => {
        console.error(`[Analytics] Error in fbq(${command})`, error)
        return undefined // Return a resolved promise to prevent unhandled rejections
      })
    }

    // If it's not a promise, just return a resolved promise
    return Promise.resolve(result)
  } catch (error) {
    console.error(`[Analytics] Exception in fbq(${command})`, error)
    return Promise.resolve() // Return resolved promise to prevent unhandled rejections
  }
}

// Simple event tracking hook
export function useTrackEvent() {
  // Track if fbq is initialized
  const [isFbInitialized, setIsFbInitialized] = useState(false)

  // Check for FB initialization on mount
  useEffect(() => {
    // Skip in SSR
    if (typeof window === 'undefined') {
      return
    }

    // Check if FB is already initialized
    const isFbReady = typeof window.fbq === 'function'

    if (isFbReady) {
      console.log('[Analytics] FB Pixel already initialized')
      setIsFbInitialized(true)
      return
    }

    // If not initialized, check periodically until it is
    const checkInterval = setInterval(() => {
      if (typeof window.fbq === 'function') {
        console.log('[Analytics] FB Pixel has been initialized')
        setIsFbInitialized(true)
        clearInterval(checkInterval)
      }
    }, 500)

    // Cleanup
    return () => clearInterval(checkInterval)
  }, [])

  // Check consent
  const canTrack = useCallback(() => {
    if (typeof window === 'undefined') {
      return false
    }
    const consent = getConsentData()
    return !!consent?.marketing
  }, [])

  // Track any event (standard or custom)
  const track = useCallback(
    (params: {
      name: string
      properties?: Record<string, any>
      options?: {
        eventId?: string
        isHealthRelated?: boolean
        forceTrack?: boolean // Add option to force tracking even outside of funnel context
        userInfo?: {
          email?: string
          phone?: string
          firstName?: string
          lastName?: string
          [key: string]: any
        }
      }
    }) => {
      // Enhanced checks for tracking readiness
      if (!canTrack()) {
        console.log('[Analytics] Cannot track - no consent')
        return
      }

      if (typeof window === 'undefined') {
        console.log('[Analytics] Cannot track - SSR context')
        return
      }

      if (!window.fbq) {
        console.error('[Analytics] Cannot track - fbq not available')

        // Add retry mechanism with exponential backoff
        let retryCount = 0
        const maxRetries = 3
        const retryTracking = () => {
          if (retryCount < maxRetries) {
            const delay = Math.pow(2, retryCount) * 500 // 500ms, 1000ms, 2000ms
            console.log(`[Analytics] Retrying event tracking in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`)

            setTimeout(() => {
              retryCount++
              if (window.fbq) {
                console.log('[Analytics] fbq now available, retrying tracking')
                // Recursively call the tracking function again
                track(params)
              } else {
                retryTracking()
              }
            }, delay)
          }
        }

        retryTracking()
        return
      }

      // Get event info
      const { name, properties = {}, options = {} } = params

      // Add debug logging for BookConsultation vs book_consultation events
      if (name.toLowerCase().includes('consultation')) {
        console.log('[CRITICAL DEBUG] Consultation event name check:', {
          rawName: name,
          isCaseCorrect: name === 'BookConsultation',
          properties: JSON.stringify(properties),
        })
      }

      // Deduplicate events based on name and source
      // Create a deduplication key from event name and source
      const source = properties.event_source || 'unknown'
      const dedupKey = `${name}:${source}`
      const now = Date.now()

      // Check if we've sent this event recently
      if (recentEvents.has(dedupKey)) {
        const lastSent = recentEvents.get(dedupKey) || 0
        if (now - lastSent < DEDUPLICATION_WINDOW) {
          console.log(`[Analytics] Skipping duplicate event ${name} from ${source} (sent ${now - lastSent}ms ago)`)
          return
        }
      }

      // Update the recent events map
      recentEvents.set(dedupKey, now)

      // Clean up old entries periodically
      if (recentEvents.size > 50) {
        const entries = Array.from(recentEvents.entries())
        const oldEntries = entries.filter(([_, timestamp]) => now - timestamp > DEDUPLICATION_WINDOW * 2)
        oldEntries.forEach(([key]) => recentEvents.delete(key))
      }

      // IMPORTANT: For BookLastMinute events, only allow tracking:
      // 1. When inside the booking flow (/book/*)
      // 2. OR when explicitly forcing tracking (e.g., for card clicks on homepage)
      // 3. OR when the event is a standard event like PageView
      if (
        name === 'BookLastMinute' &&
        !options.forceTrack &&
        !isInBookingFlow() &&
        !properties.event_source?.includes('last-minute-card')
      ) {
        console.log('[Analytics] Skipping BookLastMinute event outside of booking flow')
        return
      }

      // Debug logging for forceTrack option
      if (name === 'BookLastMinute') {
        console.log('[DEBUG] BookLastMinute event tracking check:', {
          forceTrack: !!options.forceTrack,
          isInBookingFlow: isInBookingFlow(),
          eventSource: properties.event_source,
          willTrack: true,
        })
      }

      const eventOptions = options.eventId ? { eventID: options.eventId } : undefined

      // CRITICAL FIX: Use exact event names with no transformation for custom events
      // For known event types, use direct mapping to avoid any transformation issues
      let metaEventName
      let useTrackCustom = false

      // Direct mapping for our custom events
      if (name === 'BookLastMinute') {
        metaEventName = 'BookLastMinute'
        useTrackCustom = true
      } else if (name === 'BookConsultation') {
        metaEventName = 'BookConsultation'
        useTrackCustom = true
      } else if (name === 'Book') {
        metaEventName = 'Book'
        useTrackCustom = true
      } else if (name === 'initiate_checkout_treatment') {
        metaEventName = 'InitiateCheckoutTreatment'
        useTrackCustom = true
      } else if (name === 'purchase_treatment') {
        metaEventName = 'PurchaseTreatment'
        useTrackCustom = true
      } else {
        // For standard events
        metaEventName = name.charAt(0).toUpperCase() + name.slice(1).replace(/_([a-z])/g, (g) => g[1].toUpperCase())
        useTrackCustom = !standardEvents.includes(metaEventName)
      }

      console.log(`[Analytics] Tracking ${useTrackCustom ? 'custom' : 'standard'} event: ${metaEventName}`, properties)

      // Core setup mode (for health-related content)
      if (options.isHealthRelated) {
        // Restrict properties for health-related content
        const allowedProps: Record<string, any> = {
          content_type: properties.content_type,
          content_id: properties.content_id,
          content_name: properties.content_name,
        }

        // Add discount for last minute events
        if (name === 'BookLastMinute' && properties.discount) {
          allowedProps.discount = properties.discount
        }

        // Add monetary values only for purchase events
        if ((name === 'purchase' || name === 'purchase_treatment') && properties.value) {
          allowedProps.value = properties.value
          allowedProps.currency = properties.currency || 'EUR'
        }

        // Add funnel step and other non-sensitive properties
        if (properties.booking_step) {
          allowedProps.booking_step = properties.booking_step
        }
        if (properties.funnel_step) {
          allowedProps.funnel_step = properties.funnel_step
        }
        if (properties.funnel_name) {
          allowedProps.funnel_name = properties.funnel_name
        }
        if (properties.event_source) {
          allowedProps.event_source = properties.event_source
        }

        // Add selected data parameters when available
        if (properties.selected_doctor) {
          allowedProps.selected_doctor = properties.selected_doctor
        }
        if (properties.selected_date) {
          allowedProps.selected_date = properties.selected_date
        }
        if (properties.selected_time) {
          allowedProps.selected_time = properties.selected_time
        }

        // Track the event with allowed properties using the safe wrapper
        if (useTrackCustom) {
          safeFbq('trackCustom', metaEventName, allowedProps, eventOptions)
          console.log(`[Analytics] TrackCustom sent: ${metaEventName}`, allowedProps)
        } else {
          safeFbq('track', metaEventName, allowedProps, eventOptions)
          console.log(`[Analytics] Track sent: ${metaEventName}`, allowedProps)
        }
      } else {
        // Regular tracking (non-health related) using the safe wrapper
        if (useTrackCustom) {
          safeFbq('trackCustom', metaEventName, properties, eventOptions)
          console.log(`[Analytics] TrackCustom sent (non-health): ${metaEventName}`)
        } else {
          safeFbq('track', metaEventName, properties, eventOptions)
          console.log(`[Analytics] Track sent (non-health): ${metaEventName}`)
        }
      }
    },
    [canTrack],
  )

  // Track page view
  const trackPageView = useCallback(
    (path?: string) => {
      if (!canTrack()) {
        return
      }
      if (typeof window === 'undefined' || !window.fbq) {
        return
      }

      // Use safe wrapper for fbq calls
      safeFbq('track', 'PageView', path ? { path } : undefined)
      console.log('[Analytics] PageView tracked:', path || window.location.pathname)
    },
    [canTrack],
  )

  // Identify user with user info
  const identify = useCallback(
    (userInfo: { email?: string; phone?: string; firstName?: string; lastName?: string; [key: string]: any }) => {
      if (!canTrack()) {
        return
      }
      if (typeof window === 'undefined' || !window.fbq) {
        return
      }

      // For Meta Pixel, there's no dedicated identify call,
      // but we can use Advanced Matching when tracking events
      console.log('[Analytics] User identified for enhanced matching:', {
        hasEmail: !!userInfo.email,
        hasPhone: !!userInfo.phone,
        hasName: !!(userInfo.firstName || userInfo.lastName),
      })
    },
    [canTrack],
  )

  return {
    track,
    trackPageView,
    identify,
  }
}

// Helper function to determine if we're in a booking flow
export function isInBookingFlow(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return window.location.pathname.includes('/book/')
}
